import type { Asset, AssetType } from "schemas/generated/asset";

import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { selectStoreDoesNotHaveReadFilesAccess } from "@editor/reducers/core-reducer";
import { useEditorSelector } from "@editor/store";
import { assetRouter } from "@editor/trpc/asset";

import { skipToken } from "@tanstack/react-query";

import { AssetsGrid } from "../AssetsGrid";
import { EmptyState } from "./EmptyState";
import { NoSearchResults } from "./NoSearchResults";

const INITIAL_ASSETS_TO_LOAD = 16;

export type ShopifyAssetsGridProps = {
  assetTypes?: AssetType[];
  searchTerm: string;
  onAssetSelected?: (asset: Asset) => void;
};

export const ShopifyAssetsGrid: React.FC<ShopifyAssetsGridProps> = ({
  assetTypes = ["image", "video"],
  searchTerm,
  onAssetSelected,
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const doesNotHaveReadFilesAccess = useEditorSelector(
    selectStoreDoesNotHaveReadFilesAccess,
  );
  const { data, isLoading, fetchNextPage, hasNextPage } =
    assetRouter.listShopify.useInfiniteQuery(
      workspaceId && projectId && !doesNotHaveReadFilesAccess
        ? {
            workspaceId,
            projectId,
            types: assetTypes,
            limit: INITIAL_ASSETS_TO_LOAD,
            search: searchTerm,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );
  const assets = React.useMemo(
    () => data?.pages?.flatMap((page) => page.assets) ?? [],
    [data],
  );
  return (
    <AssetsGrid.Root
      assets={assets}
      searchTerm={searchTerm}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      fetchNextPage={() => void fetchNextPage()}
      emptyState={
        searchTerm ? (
          <NoSearchResults searchTerm={searchTerm} />
        ) : (
          <ShopifyAssetEmptyState />
        )
      }
      shouldAllowActions={false}
      onAssetSelected={onAssetSelected}
    >
      <AssetsGrid.ListDetailed />
    </AssetsGrid.Root>
  );
};

const ShopifyAssetEmptyState: React.FC = () => {
  return (
    <EmptyState>
      <div className="header-h5">No Shopify assets found</div>
      <div className="text-sm">
        {" "}
        View your Shopify Files here, or upload through Replo for faster load
        times (recommended).
      </div>
    </EmptyState>
  );
};
